import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

/**
 * Floating install prompt for PWA.
 * - Listens for `beforeinstallprompt` (Chrome/Edge/Android)
 * - Shows a manual hint for iOS Safari (no API support)
 * - Dismissable, remembers choice for 7 days via localStorage
 */
const DISMISS_KEY = 'athinappy-pwa-dismiss-until';
const DISMISS_DAYS = 7;

const isIos = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissUntil && Date.now() < dismissUntil) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS doesn't fire beforeinstallprompt — show manual hint after a moment
    if (isIos()) {
      const t = setTimeout(() => {
        setShowIosHint(true);
        setVisible(true);
      }, 2000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', handler);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 bg-white border border-[#E5E5E5] shadow-lg rounded-lg p-4"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-[#8C8C8C] hover:text-[#1A1A1A]"
        aria-label="Dismiss"
        data-testid="pwa-dismiss-button"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-lg bg-[#4A5D4E] flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#1A1A1A] font-['Manrope']">Install ATHINAPPY</p>
          {showIosHint ? (
            <p className="text-xs text-[#595959] mt-1">
              Tap the <span className="font-medium">Share</span> icon in Safari, then
              <span className="font-medium"> Add to Home Screen</span> to install.
            </p>
          ) : (
            <>
              <p className="text-xs text-[#595959] mt-1">
                Get the app for faster access and offline support.
              </p>
              <Button
                size="sm"
                className="bg-[#4A5D4E] hover:bg-[#3D4D40] mt-3 h-8"
                onClick={handleInstall}
                data-testid="pwa-install-button"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Install
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
